// Engine canvas isometrik 2D — grid 60x60, tile 64x32, z-layering
// SEMUA bangunan dilukis prosedural via fungsi JavaScript (paintCode), bukan sprite gambar.
// paintCode = string fungsi JS yang menerima (ctx, P, U) dan melukis bangunan:
//   P = { top:[x,y], right:[x,y], bottom:[x,y], left:[x,y], height } — sudut footprint layar
//   U = { zoom, floors, wall:[r,g,b], roof:[r,g,b], cssCol, shade }
// Modul pendukung: buildingPaint (pelukis), spriteCache (cache sprite), occlusion (mask mover),
// terrain (tebing/rumput/grid), isoOrder (urutan gambar), worldAnalysis (sungai/landasan),
// atmosphere (langit lerp), editorOverlay (ghost/hover/HUD), ambient/skyRender/river/plane/nightLights.
import { projectIso, screenToGrid, TILE_W, TILE_H } from "./iso";
import { cssCol } from "./color";
import { paintRoadsGen } from "./road";
import { paintRunways } from "./runway";
import { hashN } from "./sky";
import { refHeight } from "./paint";
import { createMovers } from "./movers";
import { createTraffic } from "./traffic";
import { createAmbient } from "./ambient";
import { createSkyRender } from "./skyRender";
import { createRiver } from "./river";
import { createPlane } from "./plane";
import { createLampGlow, drawHeadlights } from "./nightLights";
import { paintDefault } from "./buildingPaint";
import { createSpriteCache } from "./spriteCache";
import { createOcclusion } from "./occlusion";
import { drawCliff, groundTiles, drawGridLines } from "./terrain";
import { createIsoOrder } from "./isoOrder";
import { analyzeWater, analyzeRunway } from "./worldAnalysis";
import { createAtmos, stepAtmos } from "./atmosphere";
import { drawOverlay, drawCityClock } from "./editorOverlay";
// Re-export agar import lama (CityEditor/Admin) tetap bekerja
export { paintBuildingAt, compilePaint } from "./paint";
export { skyAt, hashN } from "./sky";

export const GRID_SIZE = 60;
// Batas zoom kamera: MIN cukup kecil agar seluruh pulau muat di layar HP portrait (390px ≈ zoom .045)
const MIN_ZOOM = 0.04, MAX_ZOOM = 2.5;

// State kota kosong (pioneer state)
export function emptyState() {
  return { gridSize: GRID_SIZE, roads: [], water: [], buildings: [], runways: [] };
}

// Buat renderer terikat pada 1 canvas
export function createEngine(canvas) {
  const mainCtx = canvas.getContext("2d");
  let ctx = mainCtx; // dialihkan ke kanvas offscreen saat membangun ulang dunia
  const cam = { x: 0, y: 0, zoom: 1 };
  let dpr = 1;

  // Dunia statis (tebing + tanah + jalan + sungai + bangunan) di-cache di kanvas
  // offscreen dan hanya digambar ulang saat state/kamera berubah. Langit, awan,
  // hujan, bintang & overlay malam digambar tiap frame (murah).
  let world = null;
  let worldStamp = null;
  // Saat kamera bergerak (geser/cubit), dunia cache TIDAK dibangun ulang tiap frame —
  // bitmap lama digambar dengan geser+skala (worldShift), lalu dibangun ulang begitu
  // gestur berhenti ≥ SETTLE_MS (atau drift terlalu jauh). Ini kunci 60 fps di HP.
  const SETTLE_MS = 110;
  let camMoveT = 0, lastCam = { x: NaN, y: NaN, zoom: NaN }, worldShift = null;
  // Bitmap dunia digambar LEBIH LEBAR dari layar (margin MARGIN_K × ukuran layar tiap sisi) sehingga
  // geser jari biasa tidak perlu membangun ulang sama sekali (bitmap hanya digeser); rebuild hanya saat
  // zoom berubah (setelah gestur berhenti) atau tepi area yang tergambar hampir terlihat.
  const MARGIN_K = 0.6;
  let worldM = { x: 0, y: 0 }; // margin (px CSS) bitmap dunia saat ini
  let lamps = []; // posisi kepala lampu jalan (untuk pendar malam per-frame)
  const lampGlow = createLampGlow(); // lapisan pendar lampu jalan (pra-render, 1 blit per frame)
  let headlights = []; // sorot lampu depan mobil (posisi+arah+alpha per frame)
  // Jam kota simulasi (cepat): 1 hari kota = CITY_DAY_SEC detik nyata. Menggerakkan
  // kepadatan lalu lintas (jam sibuk pagi/sore, sepi tengah malam) — terpisah dari mode langit.
  const CITY_DAY_SEC = 480;
  const city = { hour: 6.5 };
  // Mode langit: auto (jam nyata + cuaca acak) | day | night | rain
  let skyMode = "auto";
  // Nilai atmosfer yang ter-lerp mulus per frame (transisi senja/fajar/hujan)
  const atmos = createAtmos();
  const nightQ = () => Math.round(atmos.dark * 4) / 4;

  let bldTileMap = {}; // peta tile footprint → {b, def} untuk oklusi per-mover
  // Sprite cache bangunan & oklusi mover (mask siluet) — modul terpisah
  const sprites = createSpriteCache({ getDpr: () => dpr, getZoom: () => cam.zoom, getNightQ: nightQ, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM });
  const profile = sprites.profile;
  const occl = createOcclusion({ canvas, cam, gridToScreen, getDpr: () => dpr, getCtx: () => ctx, getBldTileMap: () => bldTileMap });
  const drawOccluded = occl.drawOccluded;
  const isoBuildingOrder = createIsoOrder();

  // Subsistem warga (pejalan kaki) — movers.js; lalu lintas kendaraan darat — traffic.js.
  // drawOccluded: gambar mover lalu "hapus" bagian yang tertutup bangunan di depannya.
  const moversSys = createMovers({
    gridToScreen, cam, city,
    get atmos() { return atmos; },
    drawOccluded: (gx, gy, r, fn) => drawOccluded(gx, gy, r, fn)
  });
  const trafficSys = createTraffic({
    gridToScreen, cam, city,
    get atmos() { return atmos; },
    headlights, gridSize: GRID_SIZE,
    drawOccluded: (gx, gy, r, fn) => drawOccluded(gx, gy, r, fn)
  });
  // Subsistem per-frame (dipisah ke modul): langit/cuaca, kehidupan sungai (kilau+perahu), pesawat
  const subCtx = { gridToScreen, origin, cam, gridSize: GRID_SIZE, get atmos() { return atmos; } };
  const skySys = createSkyRender(subCtx);
  const riverSys = createRiver(subCtx);
  const planeSys = createPlane(subCtx);
  // Lapisan ambien (awan di atas kota, burung, ikan) — ambient.js
  const ambientSys = createAmbient({ gridToScreen, origin, cam, gridSize: GRID_SIZE, get atmos() { return atmos; } });
  // Animasi kehidupan (awan/burung/ikan/perahu/kendaraan/warga/pesawat) bisa dijeda dari editor.
  // animT = waktu animasi yang hanya berjalan saat aktif → saat dilanjutkan tidak ada lompatan.
  let animOn = true, animT = 0;
  let lastArgs = null; // { state, defs, opts }
  let moverKey = null;
  let lastT = 0;
  let rafId = 0;
  let showHud = true;     // HUD jam kota di kanvas (halaman publik HP menggantinya dengan pil React)

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(canvas.clientWidth * dpr);
    canvas.height = Math.round(canvas.clientHeight * dpr);
    mainCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function originFor(x, y, zoom) {
    return {
      x: canvas.clientWidth / 2 + x,
      y: canvas.clientHeight / 2 - (GRID_SIZE * TILE_H * zoom) / 4 + y
    };
  }
  // camRef = kamera yang dipakai proyeksi: normalnya `cam`; saat irisan build berjalan = snapshot kamera build
  let camRef = cam;
  function origin() { return originFor(camRef.x, camRef.y, camRef.zoom); }

  function gridToScreen(gx, gy, z = 0) {
    const [px, py] = projectIso(gx, gy, z);
    const o = origin();
    return [o.x + px * camRef.zoom, o.y + py * camRef.zoom];
  }

  function toGrid(sx, sy) {
    const o = origin();
    const rx = (sx - o.x) / cam.zoom;
    const ry = (sy - o.y) / cam.zoom;
    return screenToGrid(rx, ry);
  }

  // Gambar diamond tile (gx,gy). Pusat tile sebenarnya = gridToScreen(gx+0.5, gy+0.5),
  // karena projectIso(gx,gy) adalah SUDUT ATAS tile, bukan tengahnya.
  // od = overdraw px untuk menghapus garis tipis antar-tile (anti-aliasing).
  function tile(gx, gy, fill, od = 0) {
    const T = gridToScreen(gx, gy), R = gridToScreen(gx + 1, gy), B = gridToScreen(gx + 1, gy + 1), L = gridToScreen(gx, gy + 1);
    if (od) {
      T[1] -= od; R[0] += od; B[1] += od; L[0] -= od;
    }
    ctx.fillStyle = fill;
    ctx.beginPath();
    ctx.moveTo(...T); ctx.lineTo(...R); ctx.lineTo(...B); ctx.lineTo(...L);
    ctx.closePath();
    ctx.fill();
  }

  function lerpPt(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]; }

  // Blit sprite bangunan (dilukis sekali per bucket zoom/tingkat malam) ke kanvas dunia
  function building(b, def) {
    const f = camRef.zoom, sp = sprites.bldSprite(b, def, sprites.bucketZoom(f), nightQ());
    const [ax, ay] = gridToScreen(b.x, b.y);
    ctx.drawImage(sp.cv, ax + sp.dx * f, ay + sp.dy * f, sp.sw * f, sp.sh * f);
  }

  // Bangun ulang kanvas dunia — GENERATOR beririsan: digambar ke kanvas cadangan (back) dengan snapshot
  // kamera, dijalankan beberapa ms per frame (stepBuild) lalu ditukar saat selesai → tidak ada hentakan
  // satu frame panjang saat berhenti geser/cubit di HP. Build pertama/ganti ukuran dijalankan sinkron.
  let back = null, buildGen = null, buildSnap = null;
  function* buildWorldGen(state, defs, opts) {
    const tBuild0 = performance.now();
    const W = canvas.clientWidth, H = canvas.clientHeight;
    const snap = buildSnap, cz = snap.zoom;
    // Margin dibatasi agar luas bitmap ≤ ~12 MP (batas kanvas Safari 16,7 MP; desktop lebar/dpr 2 aman)
    let mk = MARGIN_K;
    const maxPx = 12e6 / (dpr * dpr);
    while (mk > 0.05 && (W + 2 * W * mk) * (H + 2 * H * mk) > maxPx) mk -= 0.05;
    const MX = Math.round(W * mk), MY = Math.round(H * mk);
    if (!back) back = document.createElement("canvas");
    const ww = Math.round((W + 2 * MX) * dpr), wh = Math.round((H + 2 * MY) * dpr);
    if (back.width !== ww || back.height !== wh) { back.width = ww; back.height = wh; }
    const wctx = back.getContext("2d");
    wctx.setTransform(dpr, 0, 0, dpr, MX * dpr, MY * dpr); // koordinat layar; (0,0) layar = (MX,MY) bitmap
    ctx = wctx;
    ctx.clearRect(-MX, -MY, W + 2 * MX, H + 2 * MY);
    const view = { x0: -MX, y0: -MY, x1: W + MX, y1: H + MY }; // area gambar untuk culling jalan/bangunan
    const newLamps = [];

    drawCliff(ctx, gridToScreen, cz, GRID_SIZE);
    groundTiles(ctx, gridToScreen, GRID_SIZE);
    yield;

    // Sungai/air (grid): water = ["x,y", ...] — SATU WARNA seragam (tanpa papan catur).
    // Air digambar DULU, lalu jalan di atasnya: tile jalan yang menimpa air otomatis
    // menjadi JEMBATAN BETON (drawBridge) — tiang pancangnya terjun ke air yang
    // tetap terlihat di sela-sela, dan speedboat melintas di bawahnya.
    const waterSet = new Set(state.water || []);
    const roadSet = new Set(state.roads || []);
    const { waterTiles, axis: waterFlowAxis, boatLane } = analyzeWater(waterSet);
    for (const { x: wx, y: wy } of waterTiles) tile(wx, wy, cssCol([70, 142, 202]), 0.85);
    ambientSys.setWater(waterTiles, roadSet, waterFlowAxis);
    riverSys.setWater({ tiles: waterTiles, roadSet, axis: waterFlowAxis, lane: boatLane, defs });

    // Jalan (grid): roads = ["x,y", ...] — metode 3-Pass Layering (trotoar/aspal/marka)
    // waterSet diteruskan: tile jalan di atas air = jembatan (pass 3 di road.js),
    // tiang lampu tidak dipasang di atas jembatan.
    const roadEnv = { gridToScreen, zoom: cz, hashN, lerpPt, lamps: newLamps, waterSet, view, sliceTiles: 60 };
    const tRoad0 = performance.now();
    yield* paintRoadsGen(ctx, roadSet, roadEnv);
    profile.roads = performance.now() - tRoad0;
    yield;

    // Landasan pacu (grid): runways = ["x,y", ...] — permukaan aspal polos tanpa
    // trotoar/marka jalan, marka tengah putih putus-putus + garis tepi + nomor.
    const runwaySet = new Set(state.runways || []);
    if (runwaySet.size) paintRunways(ctx, runwaySet, { gridToScreen, zoom: cz });
    const planeDef = (defs || []).find(d => d.bid === "pesawat_komersial" && d.paintCode) || null;
    planeSys.setRunway(analyzeRunway(runwaySet), planeDef);

    if (opts.showGrid) drawGridLines(ctx, gridToScreen, GRID_SIZE);

    // Bangunan — urutan gambar = urutan kedalaman isometrik yang BENAR untuk tapak
    // multi-tile: B di depan A bila tapak B dimulai setelah tapak A berakhir pada sumbu
    // x ATAU y (B.x ≥ A.x+A.w || B.y ≥ A.y+A.h), hanya dievaluasi untuk pasangan yang
    // kotak layarnya bersinggungan → diurut topologis (Kahn, tie-break x+y).
    // BUG LAMA: sort x+y titik anchor saja → bangunan lebar/panjang kadang tertimpa
    // bangunan kecil yang sebenarnya berada di belakangnya.
    const defById = {};
    for (const d of (defs || [])) defById[d.bid] = d;
    const blds = isoBuildingOrder(state.buildings || [], defById, defs);
    const tBld0 = performance.now();
    let nBld = 0;
    const unik = new Map();   // bangunan unik yang terlihat (untuk pra-render bucket tetangga)
    // Indeks bangunan per tile footprint untuk pencarian okluder mover (O(1)).
    const tileMap = {};
    const CULL = 80; // px — bangunan di luar layar (± margin) tidak perlu dilukis ke cache
    let sinceYield = 0;
    for (const b of blds) {
      if (++sinceYield >= 12) { sinceYield = 0; yield; }
      const def = defById[b.bid];
      let w = def?.w || 1, h = def?.h || 1;
      if ((b.rot || 0) % 2 === 1) [w, h] = [h, w];
      for (let ix = 0; ix < w; ix++) for (let iy = 0; iy < h; iy++) {
        tileMap[(b.x + ix) + "," + (b.y + iy)] = { b, def };
      }
      // Culling viewport: kotak layar tapak + tinggi gedung (plus overhang pohon/papan nama)
      const [lx] = gridToScreen(b.x, b.y + h), [rx] = gridToScreen(b.x + w, b.y);
      const [, ty] = gridToScreen(b.x, b.y), [, by] = gridToScreen(b.x + w, b.y + h);
      const top = ty - refHeight(def?.floors ?? 1, w, h, cz) - 40 * cz;
      if (rx < view.x0 - CULL || lx > view.x1 + CULL || by < view.y0 - CULL || top > view.y1 + CULL) continue;
      building(b, def); nBld++;
      const uk = `${def?.bid}|${b.vid || ""}|${b.rot || 0}|${b.signEdit ? JSON.stringify(b.signEdit) : ""}`;
      if (!unik.has(uk)) unik.set(uk, { b, def });
    }
    sprites.schedulePrewarm([...unik.values()]);
    profile.buildings = performance.now() - tBld0; profile.nBuildings = nBld; profile.total = performance.now() - tBuild0; profile.zoom = cz;
    // oklusi mover memakai bldTileMap + mask siluet (lihat occlusion.js)

    // Selesai → tukar kanvas depan/cadangan & terapkan hasil secara atomik
    [world, back] = [back, world];
    worldM = { x: MX, y: MY };
    lamps = newLamps;
    bldTileMap = tileMap;
    lampGlow.invalidate();
    worldStamp = [state, defs, snap.x, snap.y, snap.zoom, canvas.width, canvas.height, !!opts.showGrid, nightQ()];
    profile.rebuilds = (profile.rebuilds || 0) + 1;
  }
  // Mulai build baru (membatalkan yang sedang berjalan); sync=true → selesaikan sekarang juga
  function startBuild(state, defs, opts, sync) {
    buildSnap = { x: cam.x, y: cam.y, zoom: cam.zoom };
    buildGen = buildWorldGen(state, defs, opts);
    if (sync) stepBuild(Infinity);
  }
  // Jalankan irisan build maksimal budgetMs (proyeksi memakai snapshot kamera build)
  function stepBuild(budgetMs) {
    if (!buildGen) return;
    const t0 = performance.now();
    camRef = buildSnap;
    if (back) ctx = back.getContext("2d"); // lanjutkan menggambar ke kanvas cadangan (irisan pertama mengaturnya sendiri)
    let r;
    do { r = buildGen.next(); } while (!r.done && performance.now() - t0 < budgetMs);
    camRef = cam; ctx = mainCtx;
    if (r.done) buildGen = null;
  }

  // ---- Loop utama: dunia di-cache, atmosfer hidup tiap frame ----
  function frame(nowMs) {
    rafId = requestAnimationFrame(frame);
    if (!lastArgs) return;
    const frameT0 = performance.now();
    const W = canvas.clientWidth, H = canvas.clientHeight;
    if (!W || !H) return;
    ctx = mainCtx;
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) resize();

    const { state, defs, opts } = lastArgs;
    // Build pertama / ukuran kanvas berubah → sinkron (tidak ada bitmap lama yang layak ditampilkan)
    if (!worldStamp || canvas.width !== worldStamp[5] || canvas.height !== worldStamp[6]) startBuild(state, defs, opts, true);
    const ws = worldStamp;
    const baseChanged = state !== ws[0] || defs !== ws[1] || !!opts.showGrid !== ws[7] || nightQ() !== ws[8];
    if (cam.x !== lastCam.x || cam.y !== lastCam.y || cam.zoom !== lastCam.zoom) {
      camMoveT = nowMs; lastCam = { x: cam.x, y: cam.y, zoom: cam.zoom };
    }
    // transformasi bitmap lama → kamera sekarang: layar' = o' + (layar - o) * s
    const shiftFor = st => {
      if (cam.x === st[2] && cam.y === st[3] && cam.zoom === st[4]) return null;
      const s = cam.zoom / st[4], oOld = originFor(st[2], st[3], st[4]), oNew = origin();
      return { s, dx: oNew.x - oOld.x * s, dy: oNew.y - oOld.y * s };
    };
    let shift = shiftFor(ws);
    // Tepi bitmap hampir terlihat (margin terpakai ≥ 85 %) atau skala terlalu jauh → bangun ulang segera;
    // setelah gestur berhenti (≥ SETTLE_MS): bangun ulang bila zoom berubah atau margin terpakai ≥ 40 %
    // (agar geser berikutnya tidak menyentuh tepi). Geser murni yang pendek: cukup bitmap digeser, tanpa rebuild.
    const farDrift = !!shift && (Math.abs(shift.dx) > worldM.x * shift.s * 0.85 || Math.abs(shift.dy) > worldM.y * shift.s * 0.85 || shift.s < 0.7 || shift.s > 1.45);
    const settled = !!shift && nowMs - camMoveT >= SETTLE_MS && (shift.s !== 1 || Math.abs(shift.dx) > worldM.x * 0.4 || Math.abs(shift.dy) > worldM.y * 0.4);
    const snapStale = !!buildGen && (buildSnap.x !== cam.x || buildSnap.y !== cam.y || buildSnap.zoom !== cam.zoom);
    if ((baseChanged || farDrift || settled) && (!buildGen || snapStale)) startBuild(state, defs, opts, false);
    // Irisan build ±7 ms per frame (hentakan satu frame panjang diganti beberapa irisan kecil);
    // bila selesai frame ini, bitmap baru langsung dipakai → pergeseran dihitung ulang terhadap stempel baru.
    if (buildGen) { stepBuild(7); if (!buildGen) shift = shiftFor(worldStamp); }
    worldShift = shift;

    const t = nowMs / 1000;
    const dt = Math.min(0.05, lastT ? t - lastT : 0);
    lastT = t;
    if (animOn) animT += dt;
    const at = animT; // waktu animasi kehidupan (berhenti saat dijeda)

    // Atmosfer: target dari mode langit + transisi mulus (lihat atmosphere.js)
    const hour = stepAtmos(atmos, skyMode);
    // Profil per-frame (ms per bagian) untuk debug performa HP: window.__jawselEngine.debug.profile.frame
    const fp = profile.frame || (profile.frame = {}); let pt = performance.now();
    const lap = k => { const n = performance.now(); fp[k] = n - pt; pt = n; };

    skySys.drawSky(ctx, W, H, t, hour);
    skySys.drawUnderClouds(ctx, t); lap("sky");
    if (world) {
      const ww = W + 2 * worldM.x, wh = H + 2 * worldM.y;
      if (worldShift) ctx.drawImage(world, worldShift.dx - worldM.x * worldShift.s, worldShift.dy - worldM.y * worldShift.s, ww * worldShift.s, wh * worldShift.s);
      else ctx.drawImage(world, -worldM.x, -worldM.y, ww, wh);
    }
    lap("world");
    // Bayangan awan lembut jatuh di pulau (di bawah air/mover agar tidak menggelapkan entitas)
    if (animOn) ambientSys.drawShadows(ctx, at, W, H); lap("shadows");
    // Kilau arus sungai (selalu hidup), ikan melompat (di atas kilau, di bawah perahu), lalu armada perahu
    riverSys.drawFlow(ctx, t, W, H); lap("flow");
    if (animOn) ambientSys.drawFish(ctx, at, W, H);
    riverSys.drawBoats(ctx, at, W, H, animOn); lap("fishBoats");

    // Jam kota: mode "real" (peta publik) = jam nyata WIB (langit, lampu & kepadatan lalu lintas ikut jam asli);
    // mode lain = simulasi cepat (1 hari kota = CITY_DAY_SEC detik) untuk demo jam sibuk di editor.
    city.hour = skyMode === "real" ? hour : (city.hour + dt * 24 / CITY_DAY_SEC) % 24;
    headlights.length = 0;
    if (animOn) {
      // Mover digambar urut kedalaman (warga & mobil digabung) dengan oklusi mask siluet
      // bangunan di depannya (drawOccluded) — tidak ada lagi lukis-ulang bangunan.
      moversSys.update(at, dt);
      trafficSys.update(dt, at); lap("simUpdate");
      const layer = [...moversSys.list.filter(m => !m.hidden).map(m => ({ d: m.gx + m.gy, f: () => moversSys.drawOne(ctx, m, at) })),
        ...trafficSys.visible().map(m => ({ d: m.gx + m.gy, f: () => trafficSys.drawOne(ctx, m, at, W, H) }))].sort((a, b) => a.d - b.d);
      if (layer.length) {
        occl.beginMoverLayer();
        for (const it of layer) it.f();
        occl.endMoverLayer(W, H);
      }
      lap("movers");
      // Burung kecil di atas kota (di bawah pesawat & awan)
      ambientSys.drawBirds(ctx, at, W, H);
      // PESAWAT digambar setelah semua bangunan — ia selalu tampak di atas bangunan/pepohonan
      // saat terbang, tidak tertutup kebun atau gedung.
      planeSys.draw(ctx, at, dt, W, H);
      // Awan halus PALING ATAS (tembus pandang) — melintas di atas kota & pesawat
      ambientSys.drawClouds(ctx, at, W, H); lap("birdsPlaneClouds");
    } else {
      Object.assign(ambientSys.stats, { clouds: 0, birds: 0, fish: 0 });
    }
    drawOverlay(ctx, gridToScreen, GRID_SIZE, opts);
    // Selubung malam & kelabu hujan di atas dunia
    if (atmos.dark > 0.01) {
      ctx.fillStyle = `rgba(9,13,44,${(atmos.dark * 0.42).toFixed(3)})`;
      ctx.fillRect(0, 0, W, H);
    }
    // Cahaya di atas selubung malam: lentera perahu, pendar lampu jalan, sorot & rem mobil
    riverSys.drawLights(ctx, at);
    lampGlow.draw(ctx, lamps, worldStamp ? worldStamp[4] : cam.zoom, worldM, atmos, worldShift);
    drawHeadlights(ctx, headlights, cam, atmos, occl, W, H);   // sorot dioklusi siluet bangunan di depan mobil
    if (showHud) drawCityClock(ctx, city.hour);
    if (atmos.rain > 0.02) {
      ctx.fillStyle = `rgba(88,100,118,${(atmos.rain * 0.14).toFixed(3)})`;
      ctx.fillRect(0, 0, W, H);
      skySys.drawRain(ctx, W, H, t);
    }
    lap("overlayLights");
    fp.total = performance.now() - frameT0; fp.n = (fp.n || 0) + 1; fp.sum = (fp.sum || 0) + fp.total; fp.worst = Math.max(fp.worst || 0, fp.total);
  }

  // Simpan argumen terakhir & pastikan loop animasi berjalan.
  // Pemanggilan ulang cukup memperbarui argumen — frame berikutnya menyesuaikan.
  function render(state, defs, opts = {}) {
    lastArgs = { state, defs, opts };
    const mk = JSON.stringify([
      state.roads || [],
      (state.buildings || []).length,
      (defs || []).filter(d => d.category === "Karakter" || d.category === "Kendaraan").map(d => (d.bid || "") + (d.paintCode ? ":1" : ":0"))
    ]);
    if (mk !== moverKey) { moverKey = mk; moversSys.build(state, defs); trafficSys.build(state, defs); }
    if (!rafId) rafId = requestAnimationFrame(frame);
  }

  const api = {
    render, gridToScreen, toGrid, cam, paintDefault, city,
    // akses debug/uji otomatis (posisi mover, jaringan jalan)
    debug: { traffic: trafficSys, movers: moversSys, profile, occl: occl.stats, ambient: ambientSys.stats, boats: riverSys.boats, plane: planeSys.pos, get bldTileMap() { return bldTileMap; } },
    setSkyMode(m) { skyMode = m; },
    get skyMode() { return skyMode; },
    // Jeda/lanjutkan animasi kehidupan (awan, burung, ikan, perahu, kendaraan, warga, pesawat)
    setAnimasi(v) { animOn = !!v; },
    get animasi() { return animOn; },
    get dark() { return atmos.dark; },
    setHud(v) { showHud = !!v; },
    // Pas-layar: pulau (GRID x GRID + tebing di bawah + atap di atas) dimuat dalam area
    // tampilan dikurangi inset UI mengambang (px) — sadar-portrait (HP) maupun lanskap.
    fitView(inset = {}) {
      const W = canvas.clientWidth, H = canvas.clientHeight;
      const top = inset.top || 0, bottom = inset.bottom || 0, pad = inset.pad ?? 0.05;
      const availW = W * (1 - 2 * pad), availH = Math.max(120, (H - top - bottom) * (1 - 2 * pad));
      const islandW = GRID_SIZE * TILE_W, islandH = GRID_SIZE * TILE_H;
      // tebing ≈ 84*zoom+34 px & atap gedung tertinggi ≈ 1.6*TILE_H*zoom di atas tanah
      cam.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, availW / islandW, (availH - 34) / (islandH + 84 + 1.6 * TILE_H)));
      api.centerOn(GRID_SIZE / 2, GRID_SIZE / 2, W / 2, top + (H - top - bottom) / 2 - 10 * cam.zoom);
    },
    // Letakkan titik grid (gx,gy) tepat di titik layar (sx,sy) — default pusat layar.
    centerOn(gx, gy, sx, sy) {
      const W = canvas.clientWidth, H = canvas.clientHeight;
      if (sx == null) sx = W / 2;
      if (sy == null) sy = H / 2;
      const [px, py] = projectIso(gx, gy, 0);
      cam.x = sx - W / 2 - px * cam.zoom;
      cam.y = sy - H / 2 + (GRID_SIZE * TILE_H * cam.zoom) / 4 - py * cam.zoom;
    },
    pan(dx, dy) { cam.x += dx; cam.y += dy; },
    zoomAt(f) { cam.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, cam.zoom * f)); },
    // Zoom berjangkar pada titik layar (sx,sy): titik dunia di bawah jari tetap di tempat (cubit/ketuk-dua-kali).
    zoomAtPoint(f, sx, sy) {
      const W = canvas.clientWidth, H = canvas.clientHeight;
      const o = origin();
      const wx = (sx - o.x) / cam.zoom, wy = (sy - o.y) / cam.zoom;
      cam.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, cam.zoom * f));
      cam.x = sx - W / 2 - wx * cam.zoom;
      cam.y = sy - H / 2 + (GRID_SIZE * TILE_H * cam.zoom) / 4 - wy * cam.zoom;
    }
  };
  if (typeof window !== "undefined") window.__jawselEngine = api; // untuk uji otomatis
  return api;
}
